import { ListboxItem, PhoneInput, TmdxAvatar } from '@ds';
import { Controller, useFormContext } from 'react-hook-form';
import { MdOutlinePerson } from 'react-icons/md';
import { useMemo, useState } from 'react';
import { CreateNewItem } from '@features/components/CaseFormBlocks/components/types/newItem';
import { Uuid } from '@/api/types';
import { displayPhoneNumber, getContactFullName, getFullName, sortAlphabetically } from '@/utils/helpers';
import { useCountriesData } from '@/contexts/CountriesDataProvider';
import { ListboxItemHandlersType, SearchableListbox } from './SearchableListbox';

export type OpoContactInputItem = {
  id: Uuid;
  firstName?: string | null;
  lastName?: string | null;
  primaryPhone?: string | null;
  secondaryPhone?: string | null;
  photoPath?: string | null;
  isPrimary?: boolean;
  userId?: Uuid | null;
  isOpoUser?: boolean;
};

type OpoContactInputsProps = {
  contactsList: OpoContactInputItem[];
  opoId?: Uuid | null;
  disabledContact?: boolean;
  disabledContactPhone?: boolean;
  contactFieldPlaceholder: string;
  contactFieldName: string;
  phoneFieldName: string;
  createNewItem?: CreateNewItem;
  onClear?: () => void;
  isClearable?: boolean;
  onContactChange?: (contactId: string) => void;
};

type FullContact = OpoContactInputItem & {
  fullName: string;
  displayPhone: string;
};

const getContactTypeLabel = (contact: OpoContactInputItem) => {
  return contact.userId || contact.isOpoUser ? 'User' : 'Contact';
};

const getContactDisplayName = (contact: FullContact) => {
  return `${contact.fullName} (${getContactTypeLabel(contact)})`;
};

export const OpoContactInputs = ({
  contactsList,
  opoId,
  disabledContact,
  disabledContactPhone,
  contactFieldPlaceholder,
  contactFieldName,
  phoneFieldName,
  createNewItem,
  onClear,
  isClearable,
  onContactChange,
}: OpoContactInputsProps) => {
  const { control, setValue, getValues } = useFormContext();
  const { countryCodes } = useCountriesData();
  const [searchQuery, setSearchQuery] = useState('');

  const searchedContacts = useMemo(() => {
    return contactsList.filter((contact) => {
      const contains = (str?: string | null) => str?.toLowerCase().includes(searchQuery.toLowerCase());

      const fullName = getFullName(contact.firstName, contact.lastName);

      return contains(fullName) || contains(contact.primaryPhone) || contains(contact.secondaryPhone);
    });
  }, [searchQuery, contactsList]);

  const sortedContacts: FullContact[] = useMemo(() => {
    const contactsWithFullName = searchedContacts.map((contact) => ({
      ...contact,
      fullName: getContactFullName(contact.firstName, contact.lastName),
      displayPhone: displayPhoneNumber(contact.primaryPhone ?? ''),
    }));

    return sortAlphabetically<FullContact>(contactsWithFullName, 'fullName');
  }, [searchedContacts]);

  const handleOpoContactSelect = (id?: Uuid) => {
    if (!id) {
      setValue(phoneFieldName, '');
      return;
    }

    const currentContact = contactsList.find((contact) => contact.id === id);

    setValue(phoneFieldName, currentContact?.primaryPhone ?? '');
    onContactChange?.(id);
  };

  const contactId = getValues(contactFieldName);
  const contact = sortedContacts.find((item) => item.id === contactId);

  const getButtonContent = () =>
    contact ? (
      <>
        <TmdxAvatar size="md" radius="md" src={contact.photoPath ?? undefined} name={contact.fullName} />
        <span className="text-m-m text-base-5 truncate w-[220px] text-left">{getContactDisplayName(contact)}</span>
      </>
    ) : (
      <>
        <MdOutlinePerson size={24} />
        <div className="text-m-r truncate">{contactFieldPlaceholder}</div>
      </>
    );

  const getContactItem = (item: FullContact, handlers: ListboxItemHandlersType) => (
    <ListboxItem key={item.id} textValue={getContactDisplayName(item)} {...handlers}>
      <div className="flex items-center gap-2">
        <TmdxAvatar size="xl" radius="xl" src={item.photoPath ?? undefined} name={item.fullName} />

        <div className="min-w-0">
          <div className="text-m-m text-base-5 w-[220px] truncate text-wrap">{getContactDisplayName(item)}</div>
          <div className="text-s-r text-base-20">{item.displayPhone}</div>
        </div>
      </div>
    </ListboxItem>
  );

  return (
    <div className="flex gap-4">
      <div className="flex-[50%] truncate">
        <Controller
          name={contactFieldName}
          control={control}
          render={({ field: { value, onChange }, fieldState: { invalid, error } }) => (
            <>
              <SearchableListbox
                disabled={disabledContact || (!contactsList.length && !createNewItem) || !opoId}
                invalid={invalid}
                items={sortedContacts}
                selectedKeys={value ? [value] : []}
                setSearchQuery={setSearchQuery}
                onselectionchange={(keys) => {
                  const values = Array.from(keys as Set<string>);
                  const selectedValue = values[0];

                  if (!selectedValue && !isClearable) {
                    return;
                  }

                  onChange(selectedValue);
                  handleOpoContactSelect(selectedValue);
                }}
                getTriggerContent={getButtonContent}
                renderItem={(item, handlers) => getContactItem(item as FullContact, handlers)}
                createNewItem={createNewItem}
                isClearable={isClearable}
                onClear={onClear}
                isEmpty={!value}
              />

              {error?.message && <div className="text-danger-40 text-s-r mt-1 ml-1">{error.message}</div>}
            </>
          )}
        />
      </div>

      <div className="flex-[50%]">
        <Controller
          name={phoneFieldName}
          control={control}
          render={({ field: { ref, value, ...field }, fieldState: { invalid, error } }) => (
            <PhoneInput
              {...field}
              countries={countryCodes}
              disabled={disabledContactPhone}
              className="flex-1"
              selectAriaLabel="Phone Code Input"
              inputAriaLabel="Phone Number Input"
              value={value || ''}
              isInvalid={invalid}
              error={error?.message}
            />
          )}
        />
      </div>
    </div>
  );
};
